import { useMemo, type ReactElement } from 'react';
import { PageLoadingFallback } from '@/features/metrics/components/PageLoadingFallback';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { AddReleaseDialog } from '@/features/releases/components/dialogs';
import { useReleasesPageViewModel } from '@/features/releases/hooks/useReleasesPageViewModel';
import {
  releaseModeTabsSx,
  releaseModeTabSx,
  resolveReleasesStatusCardIcon,
} from '@/features/releases/pages/releaseModePresentation';
import { ReleasesTablePanel } from '@/features/releases/pages/ReleasesTablePanel';
import { DeliveryListEmptyState } from '@/features/shared/components/DeliveryListEmptyState';
import { SearchTagsDateHeaderFiltersNext } from '@/features/shared/components/entityTrends/SearchTagsDateHeaderFiltersNext';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { s } from '@/theme/tokens';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { AddIcon, Box, Button, ReleasesIcon, Tab, Tabs, Text } from '@/ui/primitives';
import type { ReleaseStatus } from '@flowqa/shared';

export const ReleasesPage = (): ReactElement => {
  const {
    activeMode,
    setActiveMode,
    modeCounts,
    searchValue,
    setSearchValue,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    projectFilterOptions,
    showProjectFilter,
    onStatusesChange,
    onTagsChange,
    onProjectsChange,
    tableFilters,
    releaseMetricCards,
    releaseMetricCardsLoading,
    showPageLoading,
    isKnownEmpty,
    addDialog,
  } = useReleasesPageViewModel();
  const tableProjectOptions = useMemo(
    () =>
      projectFilterOptions.map((option) => ({
        id: option.value,
        name: option.label,
        badgeChars: '',
        badgeColor: 'neutral' as const,
        badgeEnabled: false,
      })),
    [projectFilterOptions],
  );
  const tableNoRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'release' });

  if (showPageLoading) {
    return <PageLoadingFallback loadingMessageKey="releases" />;
  }

  return (
    <>
      <ListPageTemplate
        headerActionsMode="separate"
        title={
          <Text role="pageTitle" component="h1">
            Releases
          </Text>
        }
        titleActions={
          isKnownEmpty ? null : (
            <Button
              variant="primary"
              fullWidthOnMobile={false}
              startIcon={<AddIcon />}
              onClick={addDialog.onOpen}
            >
              Add Release
            </Button>
          )
        }
        headerActions={
          isKnownEmpty ? null : (
            <SearchTagsDateHeaderFiltersNext
              searchAriaLabel="Search releases"
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              selectedStatuses={selectedStatuses}
              statusOptions={releaseStatusOptions}
              onStatusesChange={(next) => {
                onStatusesChange(next as ReleaseStatus[]);
              }}
              selectedTags={selectedTags}
              tagOptions={tagOptions}
              tagOptionsLoading={tagOptionsLoading}
              onTagsChange={onTagsChange}
              selectedProjects={showProjectFilter ? selectedProjects : undefined}
              onProjectsChange={showProjectFilter ? onProjectsChange : undefined}
              projectOptions={showProjectFilter ? projectFilterOptions : undefined}
              range="7d"
              onRangeChange={() => undefined}
              pickerStartDay={null}
              pickerEndDay={null}
              hasCustomDates={false}
              isLatestMode={false}
              onSelectedStartDayChange={() => undefined}
              onSelectedEndDayChange={() => undefined}
              includeLatest={false}
              showDateFilter={false}
              showCustomDateRange={false}
            />
          )
        }
        summary={
          isKnownEmpty ? null : (
            <StatusMetricCardsRow
              cards={releaseMetricCards}
              loading={releaseMetricCardsLoading}
              resolveStatusCardIcon={resolveReleasesStatusCardIcon}
            />
          )
        }
        content={
          isKnownEmpty ? (
            <DeliveryListEmptyState
              actionLabel="Add Release"
              body="Create your first release to organise features and direct tests as delivery moves from in progress to released."
              icon={ReleasesIcon}
              title={getEmptyStateMessage('entity.noneYetForProject', { entity: 'release' })}
              onActionClick={addDialog.onOpen}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: s('lg') }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={activeMode}
                  onChange={(_, nextMode) => {
                    setActiveMode(nextMode as typeof activeMode);
                  }}
                  aria-label="Release delivery mode"
                  preserveScrollOnChange={false}
                  sx={releaseModeTabsSx}
                >
                  <Tab
                    label={`In Progress (${modeCounts.inProgress})`}
                    value="inProgress"
                    sx={releaseModeTabSx}
                  />
                  <Tab
                    label={`Released (${modeCounts.released})`}
                    value="released"
                    sx={releaseModeTabSx}
                  />
                </Tabs>
              </Box>
              <ReleasesTablePanel
                fixedFilters={tableFilters}
                noRowsText={tableNoRowsText}
                projectOptions={tableProjectOptions}
                releaseMode={activeMode}
                searchValue={searchValue}
                tagOptions={tagOptions}
              />
            </Box>
          )
        }
        hideHeader={isKnownEmpty}
      />
      <AddReleaseDialog
        open={addDialog.open}
        title="Add Release"
        saving={addDialog.saving}
        values={addDialog.values}
        errors={addDialog.errors}
        statusOptions={releaseStatusOptions}
        projectOptions={addDialog.projectOptions}
        showProjectSelect={addDialog.showProjectSelect}
        showStatusSelect={addDialog.showStatusSelect}
        errorMessage={addDialog.errorMessage}
        onClose={addDialog.onClose}
        onSave={addDialog.onSave}
        handlers={addDialog.handlers}
      />
    </>
  );
};
